import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SellerOnboardingAlert from "../components/SellerOnboardingAlert";
import { dealsService } from "../features/deals/service";
import type { ParsedApiError } from "../lib/http/apiErrors";
import { parseApiError } from "../lib/http/apiErrors";

const ShowcasePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [mediaFile, setMediaFile] = useState<File | null>(null);

  const [saving, setSaving] = useState(false);
  const [createdShowcaseId, setCreatedShowcaseId] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [errorInfo, setErrorInfo] = useState<ParsedApiError | null>(null);

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setMediaFile(null);
    setCreatedShowcaseId(null);
    setErrorMsg("");
    setErrorInfo(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg("");
    setErrorInfo(null);

    if (!title.trim()) {
      setErrorMsg("Please enter showcase title.");
      return;
    }

    setSaving(true);
    try {
      const created = await dealsService.submitDealListing(
        { title: title.trim(), description: description.trim() },
        mediaFile ?? undefined,
      );

      setCreatedShowcaseId(created.id);
    } catch (err) {
      const parsed = parseApiError(err, "Failed to save showcase. Please try again.");
      setErrorMsg(parsed.message);
      setErrorInfo(parsed);
    } finally {
      setSaving(false);
    }
  };

  if (createdShowcaseId) {
    return (
      <div className="page-shell flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-lg w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-3xl">✨</span>
            </div>
            <h2 className="text-xl font-bold text-gray-900">Showcase saved</h2>
            <p className="text-sm text-gray-500 mt-1 font-medium">{title.trim()}</p>
            <p className="text-xs text-gray-400 mt-2">Showcase ID: {createdShowcaseId}</p>
          </div>
          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={() => navigate("/inventory/my")}
              className="w-full py-3 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              My Inventory
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="w-full py-3 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-colors shadow-sm"
            >
              + Post another showcase
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-shell">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate("/deals/create")}
            className="text-gray-500 hover:text-gray-800 text-sm font-medium"
          >
            ← New Listing
          </button>
          <div className="h-5 w-px bg-gray-300" />
          <h1 className="text-xl font-bold text-gray-900">AI Showcase</h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        <p className="text-sm text-gray-500 mb-6">Curate your best offerings into a seller showcase.</p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <label className="block">
            <span className="block text-sm font-semibold text-gray-800 mb-1">Showcase title *</span>
            <input
              required
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm bg-white"
            />
          </label>

          <label className="block">
            <span className="block text-sm font-semibold text-gray-800 mb-1">What you offer</span>
            <textarea
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm bg-white resize-none"
            />
          </label>

          <label className="block">
            <span className="block text-sm font-semibold text-gray-800 mb-1">Upload media (image or video)</span>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm"
              onChange={(e) => setMediaFile(e.target.files?.[0] ?? null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm bg-white"
            />
            {mediaFile ? <p className="text-xs text-gray-500 mt-1">{mediaFile.name}</p> : null}
          </label>

          <div className="flex items-start gap-3 p-4 bg-indigo-50 border border-indigo-200 rounded-xl">
            <span className="text-xl shrink-0" aria-hidden>💡</span>
            <p className="text-xs text-indigo-800/90 leading-snug">
              After posting, you can link your best deals to this showcase from inventory.
            </p>
          </div>

          {errorMsg && (
            errorInfo?.isSellerOnboardingIncomplete ? (
              <SellerOnboardingAlert
                message={errorInfo.message}
                missingFields={errorInfo.missingFields}
                missingFieldsDetailed={errorInfo.missingFieldsDetailed}
                returnTo={location.pathname}
              />
            ) : (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{errorMsg}</div>
            )
          )}

          <button
            type="submit"
            disabled={saving}
            className="w-full py-3 rounded-xl text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? "Saving..." : "Post showcase"}
          </button>
        </form>
      </main>
    </div>
  );
};

export default ShowcasePage;
